import React, { createContext, useEffect, useMemo, useState } from "react";

import useAuth from "@app/common/auth/useAuth";
import { User } from "@app/common/types";
import { EnterpriseTeam } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

export type FilterBy = "workspace" | "team" | "individual" | "me";

export interface RollupFilterOption {
  id: string;
  label: string;
}

export const EMPTY_FILTER = { id: "NONE", label: "-" };
export const ALL_USERS = { id: "ALL", label: "All users" };
export const ALL_TEAMS = { id: "ALL", label: "All teams" };

interface RollupContext {
  filteredUser: { firstName: string } | null;
  filterBy: FilterBy;
  teamId?: EnterpriseTeam["id"];
  userId?: User["id"];
  teamFilter: RollupFilterOption;
  usersFilter: RollupFilterOption;
  setTeamFilter: (value: RollupFilterOption) => void;
  setUsersFilter: (value: RollupFilterOption) => void;
  reset: () => void;
  isLoading: boolean;
}

const DEFAULT_ROLLUP_CONTEXT = {
  filteredUser: null,
  filterBy: "me" as FilterBy,
  teamFilter: EMPTY_FILTER,
  usersFilter: EMPTY_FILTER,
  setTeamFilter: () => {},
  setUsersFilter: () => {},
  reset: () => {},
  isLoading: false,
};

export const RollupContext = createContext<RollupContext>(
  DEFAULT_ROLLUP_CONTEXT
);

interface RollupContextProviderProps {
  children: React.ReactNode;
}

const hasTeamFilter = (filter: RollupFilterOption) =>
  filter.id !== ALL_TEAMS.id && filter.id !== EMPTY_FILTER.id;
const hasUserFilter = (filter: RollupFilterOption) =>
  filter.id !== ALL_USERS.id && filter.id !== EMPTY_FILTER.id;

export const RollupContextProvider = ({
  children,
}: RollupContextProviderProps) => {
  const { user } = useAuth();
  const { inWorkspace, workspaceLoading, workspace } = useWorkspace();

  const [teamFilter, setTeamFilter] =
    useState<RollupContext["teamFilter"]>(EMPTY_FILTER);
  const [usersFilter, setUsersFilter] =
    useState<RollupContext["usersFilter"]>(EMPTY_FILTER);

  useEffect(() => {
    if (user && usersFilter.id === EMPTY_FILTER.id) {
      setUsersFilter({ id: user.id, label: user.fullName });
    }
  }, [Boolean(user)]);

  const getUserIdToFilter = (): User["id"] | undefined => {
    if (inWorkspace && !workspaceLoading) {
      return hasUserFilter(usersFilter) ? usersFilter.id : undefined;
    }

    return user?.id;
  };

  const getTeamIdToFilter = (): EnterpriseTeam["id"] | undefined => {
    if (inWorkspace) {
      return hasTeamFilter(teamFilter) ? teamFilter.id : undefined;
    }
  };

  const getFilterBy = (): FilterBy => {
    if (!inWorkspace || workspaceLoading) return "me";

    if (hasUserFilter(usersFilter)) {
      return usersFilter.id == user?.id ? "me" : "individual";
    }

    return hasTeamFilter(teamFilter) ? "team" : "workspace";
  };

  const getFilteredUser = (): { firstName: string } | null => {
    const filterBy = getFilterBy();
    if (filterBy === "me") return user;
    if (filterBy === "individual") {
      const user = workspace.users.find(
        (user) => user.userId === usersFilter.id
      );
      return user ?? null;
    }

    return null;
  };

  const resetFilters = () => {
    setTeamFilter(EMPTY_FILTER);
    setUsersFilter(user ? { id: user.id, label: user.fullName } : EMPTY_FILTER);
  };

  const context = useMemo(
    () => ({
      filterBy: getFilterBy(),
      filteredUser: getFilteredUser(),
      teamId: getTeamIdToFilter(),
      userId: getUserIdToFilter(),
      teamFilter,
      usersFilter,
      setTeamFilter,
      setUsersFilter,
      reset: resetFilters,
      isLoading: workspaceLoading,
    }),
    [teamFilter.id, usersFilter.id, inWorkspace]
  );

  return (
    <RollupContext.Provider value={context}>{children}</RollupContext.Provider>
  );
};
